import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../../../styles/scss/lessonLocation.scss";

// レッスン会場ページ
// 店舗を選び、今後の開催日ごとに会場名（任意で住所）を毎週入力して保存する。
// すべての開催日が自由入力（休講・振替などのメモを会場欄に入力する用途も想定）。
// 入力があればwebsiteに表示、空欄ならwebsite側で非表示になる。
// デザインは /management/ 系（customerList）に準拠し、.lesson-location-page 配下にスコープ。
function LessonLocationListPage() {
  const [studios, setStudios] = useState([]);
  const [selectedStudioId, setSelectedStudioId] = useState("");
  const [rows, setRows] = useState([]); // [{date,label,dow,off,override_id,venue_name,address}]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState("");

  // 店舗一覧の取得
  useEffect(() => {
    const fetchStudios = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/get_studios/`);
        if (!response.ok) {
          throw new Error("ネットワークレスポンスが異常です。");
        }
        const data = await response.json();
        setStudios(data);
      } catch (error) {
        console.error("店舗の取得に失敗しました", error);
      }
    };
    fetchStudios();
  }, []);

  // 都道府県ごとにまとめた選択肢（店舗数の多い順に並べる）
  const studioGroups = useMemo(() => {
    const grouped = studios.reduce((acc, studio) => {
      const key = studio.prefecture || "その他";
      if (!acc[key]) acc[key] = [];
      acc[key].push(studio);
      return acc;
    }, {});
    return Object.entries(grouped).sort((a, b) => b[1].length - a[1].length);
  }, [studios]);

  // 選択店舗の開催日＋登録済み会場を取得
  const fetchRows = useCallback(async (studioId) => {
    if (!studioId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setSavedMessage("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_lesson_venue_overrides/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ studio_id: studioId }),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRows(data.rows || []);
    } catch (error) {
      console.error("会場データの取得に失敗しました", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初期表示は長岡京（名前一致で選択。ひとまずのデフォルト）
  useEffect(() => {
    if (studios.length && !selectedStudioId) {
      const defaultStudio = studios.find((s) => s.name === "長岡京");
      if (defaultStudio) {
        const id = String(defaultStudio.id);
        setSelectedStudioId(id);
        fetchRows(id);
      }
    }
  }, [studios, selectedStudioId, fetchRows]);

  // 保存/エラーのメッセージは数秒で自動的に消す
  useEffect(() => {
    if (!savedMessage) return;
    const timer = setTimeout(() => setSavedMessage(""), 3500);
    return () => clearTimeout(timer);
  }, [savedMessage]);

  const handleStudioChange = (e) => {
    const value = e.target.value;
    setSelectedStudioId(value);
    fetchRows(value);
  };

  // 入力反映（イミュータブル）
  const handleChange = (date, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.date === date ? { ...row, [field]: value } : row))
    );
    setSavedMessage("");
  };

  // 月ごとにグループ化（表示用）
  const monthGroups = useMemo(() => {
    const groups = [];
    let current = null;
    rows.forEach((row) => {
      const month = Number(row.date.slice(5, 7));
      if (!current || current.month !== month) {
        current = { month, items: [] };
        groups.push(current);
      }
      current.items.push(row);
    });
    return groups;
  }, [rows]);

  // まとめて保存
  const handleSave = async () => {
    setSaving(true);
    setSavedMessage("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const items = rows.map((row) => ({
        date: row.date,
        venue_name: row.venue_name || "",
        address: row.address || "",
      }));
      const response = await fetch(
        `${apiUrl}/api/save_lesson_venue_overrides/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ studio_id: selectedStudioId, items }),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await fetchRows(selectedStudioId);
      setSavedMessage("保存しました。");
    } catch (error) {
      console.error("会場データの保存に失敗しました", error);
      setSavedMessage("保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lesson-location-page">
      <div className="page-header">
        <h1>レッスン会場</h1>
      </div>

      <div className="panel">
        <div className="panel-toolbar">
          <div className="studio-select">
            <select value={selectedStudioId} onChange={handleStudioChange}>
              <option value="">店舗を選択</option>
              {studioGroups.map(([prefecture, list]) => (
                <optgroup key={prefecture} label={prefecture}>
                  {list.map((studio) => (
                    <option key={studio.id} value={studio.id}>
                      {studio.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="toolbar-right">
            {savedMessage && <span className="saved-msg">{savedMessage}</span>}
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={!selectedStudioId || saving || rows.length === 0}
            >
              {saving ? "保存中..." : "保存"}
            </button>
          </div>
        </div>

        <div className="panel-body">
          {!selectedStudioId ? (
            <p className="empty-note">店舗を選択してください。</p>
          ) : loading ? (
            <p className="empty-note">読み込み中...</p>
          ) : rows.length === 0 ? (
            <p className="empty-note">今後の開催日が見つかりませんでした。</p>
          ) : (
            <>
              {monthGroups.map((group) => (
                <div key={group.month} className="month-block">
                  <div className="venue-head">
                    <div className="head month">{group.month}月</div>
                    <div className="head">会場名</div>
                    <div className="head">住所</div>
                  </div>
                  <div className="venue-grid">
                    {group.items.map((row) => (
                    <React.Fragment key={row.date}>
                      <div className="date">
                        {row.label}
                        <span className="dow"> ({row.dow})</span>
                      </div>
                      <input
                        type="text"
                        placeholder="会場名を入力"
                        value={row.venue_name || ""}
                        onChange={(e) =>
                          handleChange(row.date, "venue_name", e.target.value)
                        }
                      />
                      <input
                        type="text"
                        placeholder="住所を入力"
                        value={row.address || ""}
                        onChange={(e) =>
                          handleChange(row.date, "address", e.target.value)
                        }
                      />
                    </React.Fragment>
                    ))}
                  </div>
                </div>
                ))}
              </>
            )}
        </div>
      </div>
    </div>
  );
}

export default LessonLocationListPage;
